/**
 * Shared modal for Football 3D event results: event header + full list of markets with outcomes and odds.
 * Used by FootballScheduleResultsTab (Results tab) and Football3dWeekResultsBoard (week-specific results).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { UserSessionContext } from 'features/session/userSessionStore';
import { printOdd } from 'shared/utils/common';
import { getFootball3dTeamLogoUrl } from 'shared/utils/cdn';
import { Modal } from 'shared/components/ui/modal';
import { Button } from 'shared/components/ui/button';
import { vsMarketTypeKey, vsOutcomeTypeKey } from 'lib/virtualSportsI18n';

const FOOTBALL_MARKET_IDS = [5004, 5001, 5002, 5003, 5005, 5006];
const TWO_ROW_MARKET_IDS = [5002, 5003];

function formatTime(ts) {
  if (ts == null) return '';
  const d = new Date(Number(ts) * 1000);
  const hh = d.getHours().toString().padStart(2, '0');
  const mm = d.getMinutes().toString().padStart(2, '0');
  return `${hh}:${mm}`;
}

function normalizeMarketRaw(raw) {
  if (raw == null) return [];
  const arr = Array.isArray(raw) ? raw : [raw];
  return arr.map((x) => {
    if (x != null && typeof x === 'object' && ('result' in x || 'odd' in x)) {
      return { result: String(x.result ?? ''), odd: x.odd != null ? Number(x.odd) : null };
    }
    return { result: String(x), odd: null };
  });
}

export function FootballEventResultsModal({ open, onClose, onCloseComplete, row, weekNumber }) {
  const { t } = useTranslation();
  const [{ user }] = React.useContext(UserSessionContext);
  const oddsFormat = user?.odds_format || 'decimal';

  const headerTitle = t('RESULTS') || 'Results';
  const headerDescription =
    row &&
    [
      row.event_id != null ? `#${row.event_id}` : '',
      weekNumber != null && weekNumber !== '' ? `${t('WEEK_LABEL') || 'Week'} ${weekNumber}` : '',
      row.event_timestamp != null ? formatTime(row.event_timestamp) : '',
    ]
      .filter(Boolean)
      .join(' · ');

  return (
    <Modal
      open={open}
      onClose={onClose}
      onCloseComplete={onCloseComplete}
      title={headerTitle}
      description={headerDescription || undefined}
      size="md"
      footer={
        <Button
          variant="default"
          size="lg"
          className="w-full sm:w-auto min-w-[140px] font-semibold"
          onClick={onClose}
        >
          {t('OK') || 'OK'}
        </Button>
      }
    >
      {row && (
        <div className="flex flex-col gap-4">
          {/* Team logos + VS (no event id / time here) */}
          <div className="rounded-xl border border-border bg-muted/20 p-4">
            <div className="flex items-center justify-center gap-3">
              <div className="flex flex-col items-center gap-1.5">
                <div className="w-12 h-12 rounded-full bg-muted overflow-hidden flex items-center justify-center border-2 border-border">
                  <img
                    src={getFootball3dTeamLogoUrl(row.home_team_code ?? '')}
                    alt=""
                    className="w-8 h-8 object-contain"
                    onError={(e) => { e.target.src = getFootball3dTeamLogoUrl(''); }}
                  />
                </div>
                <span className="text-xs font-semibold text-foreground truncate max-w-[80px] text-center">
                  {row.home_team_code ?? ''}
                </span>
              </div>
              <span className="text-sm font-bold text-muted-foreground px-2">vs</span>
              <div className="flex flex-col items-center gap-1.5">
                <div className="w-12 h-12 rounded-full bg-muted overflow-hidden flex items-center justify-center border-2 border-border">
                  <img
                    src={getFootball3dTeamLogoUrl(row.away_team_code ?? '')}
                    alt=""
                    className="w-8 h-8 object-contain"
                    onError={(e) => { e.target.src = getFootball3dTeamLogoUrl(''); }}
                  />
                </div>
                <span className="text-xs font-semibold text-foreground truncate max-w-[80px] text-center">
                  {row.away_team_code ?? ''}
                </span>
              </div>
            </div>
          </div>

          {/* Results list */}
          <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
            {FOOTBALL_MARKET_IDS.map((mid) => {
              const raw = row[String(mid)] ?? row[mid];
              const items = normalizeMarketRaw(raw);
              const twoRows = TWO_ROW_MARKET_IDS.includes(mid);
              const singleOutcome = items.length === 1;
              return (
                <div
                  key={mid}
                  className="rounded-lg border border-border bg-background p-3 shadow-sm"
                >
                  {items.length === 0 ? (
                    <span className="text-sm text-muted-foreground">—</span>
                  ) : singleOutcome ? (
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {t(vsMarketTypeKey(mid, false))}
                      </span>
                      <span className="text-sm font-medium text-foreground">
                        {t(vsOutcomeTypeKey(String(items[0].result), false))}
                      </span>
                      {items[0].odd != null && (
                        <span className="text-sm font-semibold tabular-nums text-foreground">
                          {printOdd(items[0].odd, oddsFormat)}
                        </span>
                      )}
                    </div>
                  ) : twoRows ? (
                    <>
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                        {t(vsMarketTypeKey(mid, false))}
                      </div>
                      <div className="flex flex-col gap-2">
                        {items.map((it, i) => (
                          <div
                            key={i}
                            className="flex items-center justify-between gap-3 py-1.5 px-2 rounded-md bg-muted/30"
                          >
                            <span className="text-sm font-medium text-foreground">
                              {t(vsOutcomeTypeKey(String(it.result), false))}
                            </span>
                            {it.odd != null && (
                              <span className="text-sm font-semibold tabular-nums text-foreground">
                                {printOdd(it.odd, oddsFormat)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                        {t(vsMarketTypeKey(mid, false))}
                      </div>
                      <div className="flex items-center justify-between gap-3 py-1.5 px-2 rounded-md bg-muted/30">
                        <span className="text-sm font-medium text-foreground">
                          {items.map((it, i) => t(vsOutcomeTypeKey(String(it.result), false))).join(', ')}
                        </span>
                        {items[0]?.odd != null && (
                          <span className="text-sm font-semibold tabular-nums text-foreground">
                            {printOdd(items[0].odd, oddsFormat)}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Modal>
  );
}
